import { OrderProduct, OrderProductComponent, OrderProductId } from '@/models/interfaces/order'
import { CtOrder, CtPacking, PackingItem } from '@/models/interfaces/content-type'
import { computed, watch } from 'vue'
import { UUID } from '@Protium/vue-ozone-utils'
import Order from '@/utils/order'
import { Status } from '@/models/status'
import { ulid } from 'ulid'

export function validate (order: { products:CtOrder['products'], packing:Required<CtOrder>['packing'] }) {
    // -----
    if (!order.packing) {
      console.warn(`Packing validation failed! Packing was "${order.packing}"`)
      return { valid: false }
    }

    const valid = Order.validate(order).packing()

    // -----

    return { valid }
}

export function parsePackingItems (products:OrderProduct[], packing: Required<CtOrder>['packing'], packingDraft?: Array<PackingItem>) {
  // console.log(packing.length)
  return packing.map<PackingItem>(bin => {
    // get order-products for bin
    const orderProducts = bin.products.reduce<Record<OrderProductId, OrderProduct | undefined>>((dict, bp) => {
      dict[bp.id] = products.find(op => op.id === bp.id)
      return dict
    }, {})

    // enrich components with order-product component data
    const components = bin.components.map((c, ci) => {
      // HOTFIXME: since this function replaces the parent with an object after the first run, we'll treat it as such
      // NOTE if a "parent" key is missing, emit a warning but atempt to look up a "parent" for display by matching component UUID & quantity
      if (!c.parent) console.warn(`Component ${c.id} (${c.quantity}) had no "parent" key to match an order-product as parent. one will be looked up by exactly matching the component UUID + component quantity combination`)
      const parent = !c.parent
        ? products.find(pi => pi.components?.find(pic => (pic.id === c.id) && (pic.quantity === c.quantity)))?.id
        : typeof c.parent === 'object' ? (c.parent as any).id : c.parent

      const match:OrderProductComponent = orderProducts[parent]?.components?.find(pc => pc.id === c.id) as OrderProductComponent
      if (!match) return

      return {...match, parent: orderProducts[parent], ean: c.ean || match.ean, sku: c.sku || match.sku, quantity: c.quantity, sys: { id: c.id }}
    }).filter(Boolean)

    return {...bin, components} as PackingItem
  }).concat(packingDraft || [])
}

export default function usePackageParser (props, drafts, selectionState) {


  // parses input to PackingItem structure, filling in missing data where needed
  const packageParser: () => Array<PackingItem> = () => {
    const products:CtOrder['products'] = props.items
    const packing:Required<CtOrder>['packing'] = props.packing

    // input can be legacy product packing, or generated packing
    // if order.packing for whatever reason does not contain all items, from order.products, this will fall back to (re-)generating a packing array from order.products, and will emit a warning

    // validate that order.packing contains all items from order.products
    // WHY you may ask. packaging calculation (with combined stock-items as order-items) is complex,
    // and the chance that an item is dropped during binning calculation, albeit very small, still exists until this system is put through it's real-world paces
    // This check is also done on the back-end, but in different stages

    const packingDraft: PackingItem[] = drafts.value.map(item => {
        return {
          id: packing[0].id,
          draft: true,
          name: packing[0].name,
          products: [{
            quantity: item.quantity,
            draft: true,
            lineid: ulid(), //probably not correct notation?
            id: item.id
          }],
          dimensions: packing[0].dimensions,
          components: [{
            ean: item.ean,
            id: item.id,
            sys: {id: item.id},
            parent: {
              draft: item.draft,
              replacement: item.replacement},
            quantity: item.quantity,
            sku: item.sku,
            status: Status.Unsourced,
            title: item.title
          }],
          weight: item.weight,
        }
    })

    // base flat dict of product id-> quantity pairs including component id->quantity pairs
    const { valid } = validate({ products, packing })
    // --- [legacy / fallback] product-based packing, mapping to product-components
    if (!packing?.length || !valid) {
      console.warn('Packing invalid! falling back to order-product based packing')
      return products.reduce<Array<PackingItem>>((packing, product) => {
        product.components.forEach(component => {
          packing.push({
            ...product,
            ...component,
            products: [{id: product.id, quantity: product.quantity}],
            // COMBAK @selection if we decide to implement fulfillment/cancelation etc. on component-level,
            // this selection query would simply have to match the specific component->parent wombo combo (instead of the parent id)
            components: [{...component, parent: product, selected: !!selectionState.value.find(s => s.id === product.id)}]
          } as any)
        })
        return packing
      }, packingDraft)
    } else {
      // enrich packing with component titles etc.
      return parsePackingItems(products, packing, packingDraft)
    }
  }

  const parsedPackages = computed(packageParser)

  return {
    packageParser,
    parsedPackages
  }
}
